import ChartContainer from "components/molecules/ChartContainer";
import { AreaChart } from "components/molecules/Charts";
import { AreaChartDatum } from "components/molecules/Charts/AreaChart";
import { ChartNoDataState } from "components/molecules/NoDataState";
import { DATA_KEY_COLORS } from "constants/chartsColors.constant";
import { useMemo, useState } from "react";
import usePlansState from "recoilStore/Building/usePlansState";
import { getDisplayInfo } from "utils/formatting";
import prepareCarbonPenaltiesCardData from "../CarbonPenalties.helper";

function CarbonPenaltiesChart() {
    const { referencePlan, comparedPlan, referencePlanName, comparedPlanName, referencedPlanId, comparedPlanId } =
        usePlansState();
    const { chartCarbonPenaltiesData } = prepareCarbonPenaltiesCardData(
        referencePlan,
        comparedPlan,
        referencePlanName,
        comparedPlanName
    );

    const [hiddenLines, setHiddenLines] = useState<Set<string>>(new Set());

    const allChartData = useMemo((): AreaChartDatum[] => {
        if (!chartCarbonPenaltiesData || chartCarbonPenaltiesData?.length === 0) return [];

        const avoidedPenaltiesReferenceData: { x: number; y: number }[] = [];
        const avoidedPenaltiesComparedData: { x: number; y: number }[] = [];

        chartCarbonPenaltiesData.forEach(({ year: x, avoidedPenaltiesReference, avoidedPenaltiesCompared }) => {
            avoidedPenaltiesReferenceData.push({
                x,
                y: avoidedPenaltiesReference || 0,
            });
            avoidedPenaltiesComparedData.push({
                x,
                y: avoidedPenaltiesCompared || 0,
            });
        });

        return [
            {
                type: "line" as const,
                id: `${referencePlanName} carbon policies savings`,
                color: DATA_KEY_COLORS.recommendations,
                data: avoidedPenaltiesReferenceData,
                formatKey: "netTotalCost",
            },

            ...(comparedPlanId !== referencedPlanId
                ? [
                      {
                          type: "line" as const,
                          id: `${comparedPlanName} carbon policies savings`,
                          color: DATA_KEY_COLORS.baseline,
                          data: avoidedPenaltiesComparedData,
                          formatKey: "netTotalCost",
                      },
                  ]
                : []),
        ];
    }, [chartCarbonPenaltiesData, referencePlanName, comparedPlanName, comparedPlanId, referencedPlanId]);

    const visibleChartData = allChartData.filter((item) => !hiddenLines.has(item.id));

    const handleLegendClick = (lineId: string) => {
        setHiddenLines((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(lineId)) {
                newSet.delete(lineId);
            } else {
                newSet.add(lineId);
            }
            return newSet;
        });
    };

    // Checking if chart has data other than 0
    const hasData = allChartData.some((chartData) => chartData.data.some((point) => point.y !== 0));

    const { unit, formatFunction } = getDisplayInfo(
        "planLifetimeCarbonCostsAvoided",
        visibleChartData.length > 0 ? visibleChartData[0].data.map(({ y }) => y) : []
    );

    if (chartCarbonPenaltiesData === null) return <ChartNoDataState title="Carbon Policies Savings" />;

    if (!hasData) return <ChartNoDataState title="Carbon Policies Savings" />;

    return (
        <ChartContainer title="Costs" exportableData={chartCarbonPenaltiesData} hideTitle={true}>
            <AreaChart
                data={visibleChartData}
                allData={allChartData}
                hiddenLines={hiddenLines}
                onLegendClick={handleLegendClick}
                yAxisInfo={{
                    formatFunction: (v: string | number) => formatFunction(v, false),
                    unit: unit || "",
                }}
                enableGridX={true}
                height={350}
            />
        </ChartContainer>
    );
}

export default CarbonPenaltiesChart;
